import { _ as __decorate, a as __metadata, B as BaseComponent, s as setAttributes } from '../index-BJd2QXLB.js';
import { css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import 'lit/directive.js';

var styles = css`.visually-hidden,
.visually-hidden-focusable:not(:focus):not(:focus-within) {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

li {
  margin: 0 var(--bsi-toolbar-item-margin-x);
  list-style-type: none;
  text-align: center;
}
li.toolbar-divider {
  width: 1px;
  min-width: 1px;
  max-width: 1px;
  height: 1.5rem;
  align-self: stretch;
  background: var(--bsi-toolbar-divider-color);
  margin-block: var(--bsi-toolbar-padding-y);
}
li.toolbar-divider[aria-orientation=horizontal] {
  width: 100%;
  min-width: 100%;
  height: 1px;
  min-height: 1px;
  max-height: 1px;
}

a {
  text-decoration: none;
}

a,
li > * {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
a,
a .toolbar-label,
li > *,
li > * .toolbar-label {
  color: var(--bsi-toolbar-link-color);
}
a it-icon, a::part(icon),
li > * it-icon,
li > *::part(icon) {
  --bsi-icon-default: var(--bsi-toolbar-icon-color);
}
a.active,
li > *.active {
  --bsi-toolbar-link-color: var(--bsi-color-text-primary);
  --bsi-toolbar-icon-color: var(--bsi-icon-primary);
}
a.disabled,
li > *.disabled {
  --bsi-toolbar-link-color: var(--bsi-color-text-disabled);
  --bsi-toolbar-icon-color: var(--bsi-icon-disabled);
  cursor: not-allowed;
}
a:not(.disabled):hover,
li > *:not(.disabled):hover {
  text-decoration: underline;
  --bsi-toolbar-link-color: var(--bsi-color-link-hover);
  --bsi-toolbar-icon-color: var(--bsi-icon-primary-hover);
}
a,
li > * {
  /* stylelint-disable */
}
a _:-ms-fullscreen, a:not(.disabled):hover,
li > * _:-ms-fullscreen,
li > *:not(.disabled):hover {
  text-decoration: none;
}

.toolbar-label {
  margin-top: var(--bsi-toolbar-label-margin-top);
  font-size: var(--bsi-toolbar-label-font-size);
  font-weight: 600;
  line-height: 1;
  display: block;
}

.badge-wrapper {
  position: relative;
  display: block;
  width: 100%;
}

.toolbar-badge {
  box-sizing: border-box;
  display: block;
  position: absolute;
  border: 1px solid var(--bsi-toolbar-badge-border-color);
  background-color: var(--bsi-toolbar-badge-background-color);
  top: var(--bsi-toolbar-badge-top);
  right: 0px;
  min-width: var(--bsi-toolbar-badge-size);
  height: var(--bsi-toolbar-badge-size);
  padding: 0 var(--bsi-toolbar-badge-padding-x);
  color: var(--bsi-toolbar-badge-text-color);
  font-size: var(--bsi-toolbar-badge-font-size);
  line-height: 1rem;
  border-radius: var(--bsi-toolbar-badge-border-radius);
  font-style: normal;
}
.toolbar-badge.no-text {
  --bsi-toolbar-badge-padding-x: 0;
}

it-dropdown::part(button) {
  display: inline-flex;
}
it-dropdown::part(icon) {
  --bsi-icon-size-s: var(--bsi-icon-size-xs);
  height: 100%;
}`;

/**
 * Toolbar Item component for individual toolbar elements.
 * - Can be a link (with href) or a button (without href)
 * - Supports icons via SVG sprites
 * - Supports optional badge indicators
 * - Supports active and disabled states
 * - Emits click events
 *
 * @element it-toolbar-item
 */
let ItToolbarItem = class ItToolbarItem extends BaseComponent {
    constructor() {
        super(...arguments);
        /**
         * Whether the item is a divider
         */
        this.divider = false;
        /**
         * Label text for the toolbar item
         */
        this.label = '';
        /**
         * Icon name to display (referenced from sprite)
         * Example: 'it-comment', 'it-camera', 'it-file'
         */
        this.icon = '';
        /**
         * URL for the link. If not provided, renders as a button
         */
        this.href = null;
        /**
         * Badge text or number to display (optional)
         * Example: '5', '42'
         */
        this.badge = null;
        /**
         * To hide the badge number visually but keep it accessible for screen readers
         */
        this.hideBadge = false;
        /**
         * Whether the item is active
         */
        this.active = false;
        /**
         * Whether the item is disabled
         */
        this.disabled = false;
        /**
         * To hide the label visually but keep it accessible for screen readers
         */
        this.hideLabel = false;
        /**
         * Aria label for accessibility when label is hidden
         */
        this.itAriaLabel = null;
        /**
         * Aria label with additional information (for badge context)
         * Example: "5 new messages"
         */
        this.labelExtended = null;
        /**
         * Whether the item has a dropdown
         */
        this.dropdown = false;
        this.handleClick = (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }
            this.dispatchEvent(new CustomEvent('it-toolbar-item-click', { detail: { label: this.label }, bubbles: true, composed: true }));
        };
    }
    /**
     * Orientation of the containing it-toolbar, used to decide the dropdown alignment
     */
    get toolbarOrientation() {
        return this.closest('it-toolbar')?.orientation ?? null;
    }
    connectedCallback() {
        super.connectedCallback?.();
        if (!this.divider) {
            this.setAttribute('role', 'listitem');
        }
    }
    renderIcon() {
        if (!this.icon)
            return null;
        return html `<it-icon name="${this.icon}" exportparts="icon" aria-hidden="true"></it-icon>`;
    }
    renderLabel() {
        if (!this.label)
            return null;
        return !this.hideLabel ? html ` <span class="toolbar-label" aria-hidden="true"> ${this.label} </span> ` : html ``;
    }
    renderBadge() {
        if (!this.badge)
            return null;
        return html `<div class="badge-wrapper" part="badge-wrapper" aria-hidden="true">
      <span class="toolbar-badge ${this.hideBadge ? 'no-text' : ''}" part="badge">
        ${!this.hideBadge ? this.badge : ''}
      </span>
    </div>`;
    }
    renderItemContent() {
        const ariaText = [this.badge ?? '', this.label ?? '', this.labelExtended ?? ''].filter(Boolean).join(' ');
        return html `
      <span aria-hidden="true"> ${this.renderBadge()} ${this.renderIcon()} ${this.renderLabel()}</span>
      <span class="visually-hidden">${ariaText}</span>
      <slot></slot>
    `;
    }
    renderTag() {
        // Button variant
        const classes = this.composeClass({
            active: this.active,
            disabled: this.disabled,
        });
        // Use link if href is provided, otherwise button
        if (this.href) {
            return html `
        <a
          href="${this.href}"
          class="${classes}"
          ?disabled="${this.disabled}"
          it-aria-label="${ifDefined(this.itAriaLabel || undefined)}"
          aria-disabled="${ifDefined(this.disabled ? this.disabled : undefined)}"
          @click="${this.handleClick}"
          part="focusable toolbar-item-element"
        >
          ${this.renderItemContent()}
        </a>
      `;
        }
        if (this.dropdown) {
            return html `<it-dropdown
        class="${classes}"
        ?disabled="${this.disabled}"
        it-aria-label="${ifDefined(this.itAriaLabel || undefined)}"
        @click="${this.handleClick}"
        part="toolbar-item-element dropdown"
        exportparts="focusable, button, icon, icon:expand-icon, dropdown-icon-expand, dropdown-button, popover"
        variant=""
        alignment=${this.toolbarOrientation === 'vertical' ? 'right-start' : 'bottom-start'}
      >
        <div slot="label">${this.renderItemContent()}</div>
        <slot name="items"></slot>
      </it-dropdown>`;
        }
        return html `
      <it-button
        class="${classes}"
        ?disabled="${this.disabled}"
        it-aria-label="${ifDefined(this.itAriaLabel || undefined)}"
        @click="${this.handleClick}"
        part="toolbar-item-element"
        exportparts="focusable, button"
      >
        ${this.renderItemContent()}
      </it-button>
    `;
    }
    render() {
        const ariaAttributes = { ...this._ariaAttributes };
        delete ariaAttributes['aria-disabled']; // Rimuove aria-disabled se presente, gestito a livello di focusable element
        delete ariaAttributes['aria-label']; // Rimuove aria-label se presente, gestito a livello di focusable element
        delete ariaAttributes['aria-orientation']; // Rimuove aria-orientation se presente, gestito a livello di it-toolbar-item
        return this.divider
            ? html `<li
          part="toolbar-item toolbar-divider"
          class="toolbar-divider"
          role="none"
          ${setAttributes(ariaAttributes)}
        ></li>`
            : html ` <li role="none" part="toolbar-item" ${setAttributes(ariaAttributes)}>${this.renderTag()}</li>`;
    }
};
ItToolbarItem.styles = styles;
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "divider", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "label", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "icon", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "href", void 0);
__decorate([
    property({ type: String, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "badge", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'hide-badge' }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "hideBadge", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "active", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'hide-label' }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "hideLabel", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'it-aria-label' }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "itAriaLabel", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'label-extended' }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "labelExtended", void 0);
__decorate([
    property({ type: Boolean, reflect: true }),
    __metadata("design:type", Object)
], ItToolbarItem.prototype, "dropdown", void 0);
ItToolbarItem = __decorate([
    customElement('it-toolbar-item')
], ItToolbarItem);

export { ItToolbarItem };
//# sourceMappingURL=it-toolbar-item.js.map
